// Module 12 — draggable floating panels (P-DESKTOP-FLOAT, 2026-07-06 pt.5). Real pointer-drag mechanics
// for panels workspace.ts marks `float:true` — user report + annotated screenshot: the old two-edge-
// drawer's stacked, scrolling panel columns looked "cut/sliced"; on desktop/laptop, everything except the
// launcher, Controls, and the Consumption/Generation charts should behave like real OS windows — freely
// draggable, brought to front on interaction. The narrow `UiElement.addEventListener(type, handler: () =>
// void)` seam carries no event object, so (like element-clearance.ts's ResizeObserver) the actual pointer
// gesture lives here in the app/ composition-root layer, calling back into the mounted workspace's narrow
// setPanelPosition/bringToFront handle (threaded in via mount.ts's onWorkspaceReady side-channel — see
// dashboards/mount.ts and dashboards/index.ts). The delta math + id-extraction are pure and unit-tested;
// the pointer-capture wiring itself is real-DOM glue exercised only at runtime (same convention as the
// rest of this directory — perf-hud.ts, element-clearance.ts).

export interface DragHandle {
  setPanelPosition(id: string, x: number, y: number): void;
  bringToFront(id: string): void;
}

export interface DraggablePanels {
  stop(): void;
}

// Pure: given the drag's starting pointer+panel position and the CURRENT pointer position, compute the
// panel's new top-left. Exported so the delta math is unit-tested without a real PointerEvent.
export function computeDraggedPosition(
  start: { pointerX: number; pointerY: number; panelX: number; panelY: number },
  currentPointer: { x: number; y: number },
): { x: number; y: number } {
  return {
    x: start.panelX + (currentPointer.x - start.pointerX),
    y: start.panelY + (currentPointer.y - start.pointerY),
  };
}

// Pure: keep at least `minVisiblePx` of a floating panel's top-left corner inside the viewport — the
// header is the ONLY drag handle, so a position that drifts entirely off-screen (a wide cascade-default
// wrap, or a position persisted from a wider/taller monitor) would otherwise be permanently unreachable
// by mouse, unlike the old drawer system this replaces (which always kept every panel inside a visible,
// scrollable column). Exported so the clamp math is unit-tested without a real `window`.
export function clampToViewport(
  pos: { x: number; y: number },
  viewport: { width: number; height: number },
  minVisiblePx = 60,
): { x: number; y: number } {
  const maxX = Math.max(0, viewport.width - minVisiblePx);
  const maxY = Math.max(0, viewport.height - minVisiblePx);
  return { x: Math.min(Math.max(pos.x, 0), maxX), y: Math.min(Math.max(pos.y, 0), maxY) };
}

// Pure: if a panel's current rect genuinely overlaps the launcher's rect, return a nudged {x,y} that
// clears it (same x, moved to just below the launcher's bottom edge); otherwise null (no nudge needed).
// workspace.ts's `cascadePos` is a pure function with no live DOM access, so it can't know the launcher's
// REAL rendered height (which varies with viewport width as its chip bar wraps onto more/fewer rows) —
// unlike `.fixed-rail`, whose CSS reads the measured `--launcher-clearance` custom property directly, a
// floating panel's `--wy` is always an explicit JS-computed pixel value, so the CSS fallback never
// applies. Confirmed live: cascade slots for the first ~7 registered floating panels land at x<624/y<476,
// which genuinely intersects the launcher's typical rendered footprint — the launcher sits at z-index 40
// (always on top by design, so it can't be the one that yields), leaving the panel's header unreachable
// underneath it, with no amount of bringToFront ever fixing it (that only competes with OTHER floating
// panels, never the launcher). Exported so the overlap math is unit-tested without a real DOM.
export function avoidLauncherOverlap(
  panelRect: { left: number; top: number; right: number; bottom: number },
  launcherRect: { left: number; top: number; right: number; bottom: number },
  margin = 8,
): { x: number; y: number } | null {
  const overlaps =
    panelRect.left < launcherRect.right && panelRect.right > launcherRect.left &&
    panelRect.top < launcherRect.bottom && panelRect.bottom > launcherRect.top;
  if (!overlaps) return null;
  return { x: panelRect.left, y: launcherRect.bottom + margin };
}

// Extract the panel id from a `wpanel-${id}` class token (workspace.ts's paintPanel convention). The id
// itself can contain hyphens (e.g. "chart-lambda"), so this matches the "wpanel-" PREFIX and takes
// everything after it, rather than naively splitting the whole className on "-".
export function panelIdFromClassName(className: string): string | null {
  const m = /(?:^|\s)wpanel-(\S+)/.exec(className);
  return m ? m[1]! : null;
}

// `window` doesn't exist under this project's node-environment unit tests (this module is otherwise
// zero-import/self-contained) — falling back to an unbounded viewport there makes clampToViewport a
// no-op instead of throwing, so the module stays callable from a test harness with a fake DOM.
function getViewportSize(): { width: number; height: number } {
  return typeof window !== "undefined"
    ? { width: window.innerWidth, height: window.innerHeight }
    : { width: Infinity, height: Infinity };
}

export function mountDraggablePanels(root: ParentNode | null, workspace: DragHandle | null): DraggablePanels {
  if (!root || !workspace) return { stop(): void { /* no-op — nothing to wire yet */ } };

  const cleanups: Array<() => void> = [];
  // Queried once, outside the per-panel loop below, since it's the same launcher for every panel's overlap
  // check. `typeof` guard rather than a bare optional call: `ParentNode` declares `querySelector`, but this
  // module's own test harness (deliberately no jsdom — see the file header) uses hand-built fake `root`
  // objects that only stub `querySelectorAll`, so a real DOM node gets its launcher, a test fake gets null.
  const launcherEl = typeof (root as { querySelector?: unknown }).querySelector === "function"
    ? (root as ParentNode).querySelector<HTMLElement>(".wks-launcher")
    : null;
  const headers = root.querySelectorAll<HTMLElement>(".wpanel.floating .wpanel-head");
  headers.forEach((header) => {
    const panelEl = header.closest<HTMLElement>(".wpanel");
    const id = panelEl ? panelIdFromClassName(panelEl.className) : null;
    if (!panelEl || !id) return;

    // Safety-net position correction — a persisted position from a wider/taller window, or an old
    // cascade default that has since wrapped past the current viewport, could otherwise leave a panel's
    // header entirely off-screen and unreachable (the header is the ONLY drag handle). Also checked:
    // genuine overlap with the always-on-top launcher (see avoidLauncherOverlap) — a low cascade-index
    // panel can otherwise land partially or fully underneath it, unreachable no matter how far bringToFront
    // raises the panel's own z-index (the launcher isn't another floating panel, it's permanently above
    // all of them by design). The launcher-avoidance nudge runs FIRST, then the viewport clamp re-checks
    // the (possibly nudged) result, so a panel nudged below the launcher can never end up pushed off the
    // bottom of the screen instead. A CLOSED panel's rect is all-zero (display:none has no box), which is
    // already within bounds, so this naturally no-ops for closed panels without an explicit open check.
    const correctPosition = (): void => {
      const rect = panelEl.getBoundingClientRect();
      const launcherRect = launcherEl?.getBoundingClientRect();
      const nudged = launcherRect ? avoidLauncherOverlap(rect, launcherRect) : null;
      const clamped = clampToViewport(nudged ?? { x: rect.left, y: rect.top }, getViewportSize());
      if (clamped.x !== rect.left || clamped.y !== rect.top) workspace.setPanelPosition(id, clamped.x, clamped.y);
    };
    // Run once immediately — covers a panel that's ALREADY open the moment this module mounts (e.g.
    // `defaultOpen:true`, or a session restored with it left open). A MutationObserver only fires on
    // changes AFTER `observe()` is called, so it can never see this already-existing state on its own.
    correctPosition();
    // ...then keep re-checking every time the panel's open/closed/collapsed state changes (workspace.ts's
    // `paintPanel` rewrites `className` on every such transition) — a panel most commonly starts CLOSED
    // and is opened LATER by the user via a launcher chip, well after this module's one-time mount; without
    // this observer the boot-time check above would only ever protect already-open panels, missing the
    // far more common "open it via the launcher" path entirely (confirmed live: a closed-at-boot panel
    // opened later kept its raw, uncorrected cascade position). `MutationObserver` doesn't exist under this
    // project's node-environment unit tests, same as `window` — guarded the same way.
    if (typeof MutationObserver !== "undefined") {
      const observer = new MutationObserver(correctPosition);
      observer.observe(panelEl, { attributes: true, attributeFilter: ["class"] });
      cleanups.push(() => observer.disconnect());
    }

    const onPointerDown = (e: PointerEvent): void => {
      if (e.button !== 0) return; // primary button/touch/pen only
      // don't hijack a click on the collapse/close buttons living in the same header
      if ((e.target as HTMLElement).closest(".wpanel-btn")) return;
      workspace.bringToFront(id);
      const rect = panelEl.getBoundingClientRect();
      const start = { pointerX: e.clientX, pointerY: e.clientY, panelX: rect.left, panelY: rect.top };
      // setPointerCapture can throw (NotFoundError) if the browser doesn't consider e.pointerId a
      // currently-active pointer — never observed from a genuine user-driven pointerdown, but cheap to
      // guard: without this try/catch an exception here would silently abort the rest of the handler,
      // meaning onMove/onUp below would never get attached and the drag would do nothing at all.
      try { header.setPointerCapture(e.pointerId); } catch { /* proceed uncaptured */ }
      const onMove = (ev: PointerEvent): void => {
        const next = computeDraggedPosition(start, { x: ev.clientX, y: ev.clientY });
        const clamped = clampToViewport(next, getViewportSize());
        workspace.setPanelPosition(id, clamped.x, clamped.y);
      };
      const onUp = (ev: PointerEvent): void => {
        try { header.releasePointerCapture(ev.pointerId); } catch { /* was never captured */ }
        header.removeEventListener("pointermove", onMove);
        header.removeEventListener("pointerup", onUp);
        header.removeEventListener("pointercancel", onUp);
      };
      header.addEventListener("pointermove", onMove);
      header.addEventListener("pointerup", onUp);
      header.addEventListener("pointercancel", onUp);
    };
    header.addEventListener("pointerdown", onPointerDown);
    header.style.cursor = "move";
    cleanups.push(() => header.removeEventListener("pointerdown", onPointerDown));
  });

  return {
    stop(): void { for (const c of cleanups) c(); },
  };
}
